import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { MenuResolverService } from './core/resolvers/menu-resolver.service';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { UnauthenticatedLayoutComponent } from './layout/unauthenticated-layout/unauthenticated-layout.component';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { ADMIN_MENU, DASHBOARD_MENU } from './shared/constants/menu';
import { DomainGuard } from '@core/guards/domain.guard';
import { RedirectingLoaderComponent } from './shared/components/redirecting-loader/redirecting-loader.component';
import { RedirectGuard } from './core/guards/redirect.guard';
import { RedirectRoutingKeyParams } from './core/constants/route-params';
import { ENDPOINTS } from './core/constants/endpoints';
import { ACCOUNT_PERMISSION_LEVEL } from './shared/enum/permissions';
import { VulnerabilitiesComponent } from './pages/vulnerabilities/vulnerabilities.component';
import { BlankLayoutComponent } from './layout/blank-layout/blank-layout.component';

const routes: Routes = [
    {
        path: 'admin',
        component: MainLayoutComponent,
        canActivate: [AuthGuard, DomainGuard],
        resolve: {
            menu: MenuResolverService,
        },
        data: {
            menuItems: ADMIN_MENU,
            featureFlagPermissionLevel: ACCOUNT_PERMISSION_LEVEL.SERVICE_ACCOUNT,
        },
        loadChildren: () => import('./features/admin/admin.module').then((m) => m.AdminModule),
    },
    {
        path: 'dashboard',
        component: MainLayoutComponent,
        canActivate: [AuthGuard, DomainGuard],
        resolve: {
            menu: MenuResolverService,
        },
        data: {
            menuItems: DASHBOARD_MENU,
        },
        loadChildren: () =>
            import('./features/dashboard/dashboard.module').then((m) => m.DashboardModule),
    },
    {
        path: 'asset',
        component: BlankLayoutComponent,
        canActivate: [AuthGuard, DomainGuard],
        loadChildren: () => import('./features/asset/asset.module').then((m) => m.AssetModule),
    },
    {
        path: 'user-settings',
        component: MainLayoutComponent,
        canActivate: [AuthGuard, DomainGuard],
        loadChildren: () =>
            import('./features/user-settings/user-settings.module').then(
                (m) => m.UserSettingsModule
            ),
    },
    {
        path: 'data-verification-tool',
        component: UnauthenticatedLayoutComponent,
        loadChildren: () =>
            import('./features/data-verification-tool/data-verification-tool.module').then(
                (m) => m.DataVerificationToolModule
            ),
    },
    {
        path: 'login',
        component: UnauthenticatedLayoutComponent,
        loadChildren: () =>
            import('./features/authentication/authentication.module').then(
                (m) => m.AuthenticationModule
            ),
    },
    {
        path: 'policies',
        component: UnauthenticatedLayoutComponent,
        children: [
            {
                path: '**',
                component: RedirectingLoaderComponent,
                canActivate: [RedirectGuard],
                data: {
                    [RedirectRoutingKeyParams.external]: ENDPOINTS.urls.policies.privacy,
                },
            },
        ],
    },
    {
        path: 'vulnerabilities',
        component: UnauthenticatedLayoutComponent,
        children: [
            {
                path: '',
                component: VulnerabilitiesComponent,
            },
        ],
    },
    {
        path: 'internal',
        component: MainLayoutComponent,
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('./features/internal/internal.module').then((m) => m.InternalModule),
    },
    {
        path: 'not-found',
        component: MainLayoutComponent,
        children: [
            {
                path: '',
                component: PageNotFoundComponent,
            },
        ],
    },
    {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
    },
    {
        path: '**',
        redirectTo: 'not-found',
        pathMatch: 'full',
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            useHash: true,
            scrollPositionRestoration: 'top',
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
